<template>
    <div class="white-box white-box-no-tab pt-2.5 md:py-5 md:px-8">
        <Title
            styleCodePage
            text="「經濟弱勢保戶保單借款優惠利率」申請"
        />
        <h4 class="py-3 md:pb-3.5 md:pt-0">
            您本次借款同時申請『經濟弱勢保戶保單借款優惠利率』方案，請於此處輸入「優惠利率申請金額」及上傳證明文件：
        </h4>
        <h5 class="py-3 md:pb-3.5 md:pt-0">
            請輸入優惠利率申請金額
        </h5>
        <div>
            <SkeletonBase
                v-if="bailoutList.length===0"
                :table="2"
            />
            <TableBase
                v-else
                :isDashed="true"
                :cursorPointer="false"
            >
                <template #thead>
                    <tr class="text-sm lg:text-base">
                        <th>
                            <span>保單號碼</span>
                            <span class="sm:hidden"> / </span>
                            <p class="sm:hidden">
                                本次欲借款金額
                            </p>
                        </th>
                        <th class="text-right hidden sm:table-cell">
                            <span class="whitespace-nowrap">本次</span>
                            <span class="whitespace-nowrap">欲借款金額</span>
                        </th>
                        <th class="text-right">
                            <span class="break">優惠利率</span>
                            <span class="whitespace-nowrap">申請金額</span>
                        </th>
                    </tr>
                </template>
                <template #tbody>
                    <tr
                        v-for="(item, index) in bailoutList"
                        :key="index"
                        class="text-base xs:text-lg md:text-base lg:text-lg text-gray-900"
                    >
                        <td class="break-all">
                            <span class="">{{ item.fullPolicyNo }}</span>
                            <p class="sm:hidden text-gray-600 sm:text-gray-900">
                                {{ addComma(item.loanAmount) }}
                            </p>
                        </td>

                        <td class="text-right  hidden sm:table-cell">
                            {{ addComma(item.loanAmount) }}
                        </td>
                        <td class="text-right">
                            <input
                                v-model.trim="item.applyAmount"
                                type="text"
                                placeholder="請輸入申請金額"
                                inputmode="numeric"
                                autocomplete="off"
                                maxlength="7"
                                class="input-base text-right w-full max-w-[9rem] md:max-w-auto md:w-[12.5rem]"
                                @input="validate(item)"
                            >
                            <p class="error-msg text-right">
                                {{ item.errorMsg }}
                            </p>
                        </td>
                    </tr>
                </template>
            </TableBase>
        </div>

        <h5 class="mt-8">
            上傳相關證明文件
        </h5>
        <ul class="text-tip mt-2 mb-3">
            <li>
                檔案類型：jpeg、jpg、tif、tiff、gif、word檔(.doc、.docx)、pdf，每個檔案大小不可超過5MB。
            </li>
        </ul>
        <div v-if="bailoutList.length===0">
            <SkeletonBase :content="3" />
        </div>
        <div v-else>
            <div
                v-for="i in 3"
                :key="i"
                class="flex flex-col sm:flex-row mb-5 sm:mb-3"
            >
                <!-- multiple -->
                <label class="flex-shrink-0 rounded bg-gray-50 w-full sm:w-36 md:w-48 h-[3.25rem] border border-gray-200 text-gray-700 flex justify-center items-center text-sm">
                    <svg
                        class="w-5 h-5 mr-1 text-gray-400"
                        viewBox="0 0 24 24"
                    >
                        <path
                            fill="currentColor"
                            d="M14,2L20,8V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V4A2,2 0 0,1 6,2H14M18,20V9H13V4H6V20H18M12,12L16,16H13.5V19H10.5V16H8L12,12Z"
                        />
                    </svg>
                    <span>選擇檔案{{ i }}</span>
                    <input
                        type="file"
                        class="hidden"
                        @change="fileChange(i, $event)"
                        @click="$event.target.value= null"
                    >
                </label>
                <p class="break-all px-3 mt-2 sm:mt-0 flex items-center justify-between sm:justify-start rounded border border-dashed border-secondary sm:border-0 w-full min-h-[3.25rem] ">
                    <!-- {{ formData.get(`file${i}`)?.name || '未選擇檔案' }} -->
                    <span>{{ list[i]?.name || '未選擇檔案' }}</span>
                    <button
                        v-if="list[i]?.name"
                        class="flex-shrink-0 btn btn-circle btn-ghost  p-0 h-[1.75rem] w-7 min-h-[1.75rem] ml-3"
                        @click="deleteFile(i)"
                    >
                        <SvgIcon
                            icon-name="close"
                            class="text-gray-400 w-5 h-5 cursor-pointer"
                        />
                    </button>
                </p>
            </div>
            <div class="mt-12 mb-8 text-center">
                <button
                    class="btn btn-accent rounded-full mx-2 my-1"
                    @click="cancelApply"
                >
                    放棄申請
                </button>
                <button
                    class="btn gradient-primary rounded-full mx-2 my-1"
                    @click="handleSubmit"
                >
                    確定
                </button>
            </div>
        </div>
        <PopupBase
            v-model:visiblePopup="visiblePopup"
            :params="paramsPopup"
            @confirm="visiblePopup= false"
            @cancel="visiblePopup= false"
        />
        <Loading :isLoading="isLoading" />
    </div>
</template>

<script>
import { ref, reactive, inject } from 'vue';
import { useRouter } from 'vue-router';
import { addComma } from '@/utils/number/addComma';
import routeName from '@/router/constant';
import { intMinMax } from '@/utils/rules/customRules';
import { getBailout, submitBailout, postAgreeBailout } from '@/apis/Pon4300Bailout.js';
import Title from '@/components/text/Title.vue';
import TableBase from '@/components/table/TableBase.vue';
import PopupBase from '@/components/dialogs/Popup.vue';
import Loading from '@/components/Loading.vue';
import dialogContnet from '@/json/dialogContent.js';
import SkeletonBase from '@/components/skeletons/SkeletonBase.vue';

export default {
    components: {
        Title,
        TableBase,
        PopupBase,
        Loading,
        SkeletonBase
    },
    setup() {
        // const route =useRoute();
        const $vfm = inject('$vfm');
        const router = useRouter();
        const isLoading = ref(false);
        const paramsPopup = ref({});
        const visiblePopup = ref(false);
        const formData = reactive(new FormData());
        const maxSize = 5 * 1024 * 1024;
        const fileExtensionList = ['jpeg', 'jpg', 'tif', 'tiff', 'gif', 'doc', 'docx', 'pdf'];
        const list = ref({});
        const bailoutList = ref([]);
        const openModal = (params, on) => {
            $vfm.show({
                component: 'DialogBase',
                params,
                on,
            });
        };

        (() => {
            isLoading.value = true;
            getBailout().then(res => {
                bailoutList.value = res.data.bailoutList.map(i => {
                    i.applyAmount = 0;
                    return i;
                });
                if (bailoutList.value.length === 0) {
                    openModal(dialogContnet.reApply, {
                        confirm() {
                            router.push({ name: routeName.HOME });
                            $vfm.hide('DialogBase');
                        }
                    });
                }
                isLoading.value = false;
            }).catch(() => {
                openModal(dialogContnet.reApply, {
                    confirm() {
                        router.push({ name: routeName.HOME });
                        $vfm.hide('DialogBase');
                    }
                });
                isLoading.value = false;
            });
        })();

        const deleteFile = (index) => {
            formData.delete(`file${index}`);
            delete list.value[index];
        };
        // 不申請直接前往OTP
        const goOTP = () => {
            router.push({ name: routeName.OTP });
        };
        const cancelApply = () => {
            isLoading.value = true;
            postAgreeBailout().then(() => {
                goOTP();
            }).catch(() => {
                isLoading.value = false;
            });
        };
        // 借款金額驗證
        const validate = (item) => {
            const { val, errorMessage } = intMinMax({
                value: item.applyAmount,
                minNum: 0,
                maxNum: item.loanAmount,
                minMaxMsg: '您輸入的優惠利率申請金額，已超過本次欲借款金額，請調整後再送出',
            });
            item.errorMsg = errorMessage;
            item.applyAmount = val;
        };

        // 上傳檔案
        const fileChange = (index, e) => {
            const file = e.target.files[0];
            if (file) {
                // 核對副檔名
                const fileExtension = file.name.split('.').reverse();
                const obj = fileExtensionList.find(i => i === fileExtension[0]);
                // 核對檔案大小
                const isOverSize = maxSize >= file.size;
                if (isOverSize && obj) {
                    formData.delete(`file${index}`);
                    formData.append(`file${index}`, file);
                    list.value[index] = file;
                } else if (!obj) {
                    visiblePopup.value = true;
                    paramsPopup.value = {
                        title: '請確認',
                        content: '文件檔案類型僅接受jpeg、jpg、tif、tiff、gif、word檔(.doc、.docx)、pdf之格式，請再次確認',
                        confirmButton: '確認',
                    };
                } else if (!isOverSize) {
                    visiblePopup.value = true;
                    paramsPopup.value = {
                        title: '請確認',
                        content: '檔案超過5MD，請上傳5MB以內的文件檔案，謝謝。',
                        confirmButton: '確認',
                    };
                }
            } else {
                formData.delete(`file${index}`);
                delete list.value[index];
                e.target.value = null;
            }
        };

        // 檢查表單
        const validateForm = () => {
            // 檢查欄位有沒有錯誤訊息
            let sum = 0;
            let countErrorMsg = 0;
            bailoutList.value.forEach(i => {
                i.errorMsg && countErrorMsg++;
                const num = i.applyAmount ? Number(i.applyAmount) : 0;
                sum += num;
            });

            if (countErrorMsg > 0) {
                visiblePopup.value = true;
                paramsPopup.value = dialogContnet.validateMsg;
                return false;
            }
            // 檢查欄位有沒有填寫金額
            if (sum <= 0 || !sum) {
                visiblePopup.value = true;
                paramsPopup.value = {
                    title: '請確認',
                    content: '請於欲申請優惠方案之保單，輸入優惠利率申請金額後，再點選確定送出。',
                    confirmButton: '確認',
                };
                return false;
            }
            // 檢查有沒有證明文件
            if (Object.keys(list.value).length === 0) {
                visiblePopup.value = true;
                paramsPopup.value = {
                    title: '提示',
                    content: '請上傳相關證明文件後，再點選確定送出。',
                    confirmButton: '確認',
                };
                return false;
            }
            return true;
        };

        // 送出表單
        const handleSubmit = () => {
            isLoading.value = true;
            if (!validateForm()) {
                isLoading.value = false;
                return;
            }
            formData.delete('bailoutList');
            const data = bailoutList.value.map(i => {
                const { fullPolicyNo, policyNo, policySeq, idDup, loanAmount, applyAmount } = i;
                return { fullPolicyNo, policyNo, policySeq, idDup, loanAmount, applyAmount };
            });
            formData.append('bailoutList', JSON.stringify(data));
            submitBailout(formData).then(() => {
                goOTP();
            }).catch(() => {
                isLoading.value = false;
            });
        };
        return {
            addComma,
            isLoading,
            formData,
            list,
            visiblePopup,
            paramsPopup,
            bailoutList,
            fileChange,
            handleSubmit,
            validate,
            cancelApply,
            deleteFile
        };
    },
};
</script>
